<template>
  <section class="content">
    <div class="container-fluid">

      <!-- headline figures -->
      <div class="row">
        <div class="col-12 col-sm-6 col-md-3">
          <div class="info-box">
            <span class="info-box-icon bg-success elevation-1"><i class="fas fa-rupee-sign"></i></span>
            <div class="info-box-content">
              <span class="info-box-text">Today's Sales</span>
              <span class="info-box-number">{{ money(s.today.revenue) }}</span>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-md-3">
          <div class="info-box">
            <span class="info-box-icon bg-info elevation-1"><i class="fas fa-receipt"></i></span>
            <div class="info-box-content">
              <span class="info-box-text">Today's Bills</span>
              <span class="info-box-number">{{ s.today.bills }}</span>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-md-3">
          <div class="info-box">
            <span class="info-box-icon bg-warning elevation-1"><i class="fas fa-chart-line"></i></span>
            <div class="info-box-content">
              <span class="info-box-text">Average Bill</span>
              <span class="info-box-number">{{ money(s.today.avg_bill) }}</span>
            </div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-md-3">
          <div class="info-box">
            <span class="info-box-icon bg-danger elevation-1"><i class="fas fa-chair"></i></span>
            <div class="info-box-content">
              <span class="info-box-text">Tables Running</span>
              <span class="info-box-number">{{ s.running_tables }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- last 14 days -->
        <div class="col-md-8">
          <div class="card">
            <div class="card-header">
              <h3 class="card-title">Last 14 Days</h3>
              <div class="card-tools text-muted">
                Month to date: <strong>{{ money(s.month.revenue) }}</strong> over {{ s.month.bills }} bills
              </div>
            </div>
            <div class="card-body">
              <div v-for="d in s.daily" :key="d.d" class="mb-2">
                <div class="d-flex justify-content-between small">
                  <span>{{ shortDate(d.d) }}</span>
                  <span class="text-muted">{{ d.bills }} bills &middot; {{ money(d.revenue) }}</span>
                </div>
                <div class="progress" style="height:8px">
                  <div class="progress-bar bg-success" :style="{ width: bar(d.revenue) }"></div>
                </div>
              </div>
              <p v-if="!s.daily.length" class="text-muted mb-0">No sales yet.</p>
            </div>
          </div>
        </div>

        <!-- splits -->
        <div class="col-md-4">
          <div class="card">
            <div class="card-header"><h3 class="card-title">Order Type (30 days)</h3></div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <tbody>
                  <tr v-for="t in s.by_type" :key="t.label">
                    <td>{{ t.label }}</td>
                    <td class="text-right text-muted">{{ t.bills }}</td>
                    <td class="text-right">{{ money(t.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="card">
            <div class="card-header"><h3 class="card-title">Payment Mode (30 days)</h3></div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <tbody>
                  <tr v-for="p in s.by_payment" :key="p.label">
                    <td>{{ p.label }}</td>
                    <td class="text-right text-muted">{{ p.bills }}</td>
                    <td class="text-right">{{ money(p.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- top dishes -->
        <div class="col-md-4">
          <div class="card">
            <div class="card-header"><h3 class="card-title">Top Dishes (30 days)</h3></div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <thead><tr><th>Dish</th><th class="text-right">Qty</th><th class="text-right">Revenue</th></tr></thead>
                <tbody>
                  <tr v-for="f in s.top_dishes" :key="f.name">
                    <td>{{ f.name }}</td>
                    <td class="text-right">{{ Math.round(f.qty) }}</td>
                    <td class="text-right">{{ money(f.revenue) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- stock alerts -->
        <div class="col-md-4">
          <div class="card">
            <div class="card-header"><h3 class="card-title">Lowest Stock</h3></div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <tbody>
                  <tr v-for="l in s.low_stock" :key="l.name">
                    <td>{{ l.name }}</td>
                    <td class="text-right"><span class="badge badge-warning">{{ Number(l.quantity).toFixed(2) }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="card">
            <div class="card-header"><h3 class="card-title">Expiring Within 30 Days</h3></div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <tbody>
                  <tr v-for="e in s.near_expiry" :key="e.name + e.batch">
                    <td>{{ e.name }}<br><small class="text-muted">Batch {{ e.batch }}</small></td>
                    <td class="text-right"><span class="badge badge-danger">{{ e.expiry_date }}</span></td>
                  </tr>
                  <tr v-if="!s.near_expiry.length"><td class="text-muted">Nothing expiring soon.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- recent bills -->
        <div class="col-md-4">
          <div class="card">
            <div class="card-header"><h3 class="card-title">Recent Bills</h3></div>
            <div class="card-body p-0">
              <table class="table table-sm mb-0">
                <thead><tr><th>#</th><th>Type</th><th>Table</th><th class="text-right">Amount</th></tr></thead>
                <tbody>
                  <tr v-for="r in s.recent" :key="r.id">
                    <td>{{ r.id }}</td>
                    <td>{{ r.type_label }}</td>
                    <td>{{ r.table_name }}</td>
                    <td class="text-right">{{ money(r.total) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
export default {
  data() {
    return {
      s: {
        today: { bills: 0, revenue: 0, tax: 0, avg_bill: 0 },
        month: { bills: 0, revenue: 0 },
        running_tables: 0,
        daily: [], top_dishes: [], by_type: [], by_payment: [],
        low_stock: [], near_expiry: [], recent: [],
      },
    };
  },
  methods: {
    money(v) {
      return 'Rs ' + Number(v || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },
    shortDate(d) {
      const x = new Date(d);
      return x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    },
    bar(v) {
      const max = Math.max.apply(null, this.s.daily.map(x => Number(x.revenue)).concat([1]));
      return Math.max(2, (Number(v) / max) * 100) + '%';
    },
    load() {
      axios.get('/api/dashboard/summary')
        .then(({ data }) => { this.s = data.data; })
        .catch(() => {});
    },
  },
  created() {
    this.load();
  },
};
</script>
